import React, { useState, useEffect, useCallback } from "react";
import { ShieldAlert, ShieldCheck, Wrench, Plus, X, Pencil, Image as ImageIcon, Trash2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const handleImageUpload = (e, setFormData) => {
  const file = e.target.files[0];
  if (file) {
    if(file.size > 2000000) {
      alert("Image is too large. Please select an image under 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setFormData(prev => ({...prev, image_url: reader.result}));
    reader.readAsDataURL(file);
  }
};

const EditEquipmentModal = ({ onClose, onEdit, eq }) => {
  const [formData, setFormData] = useState(eq);
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-sm relative shadow-2xl overflow-y-auto max-h-[90vh]">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-gold transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-gold mb-6">Edit Equipment</h2>
        <form onSubmit={(e) => { e.preventDefault(); onEdit(formData); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Machine Name</label>
            <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Replace Image (Optional)</label>
            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setFormData)} className="w-full bg-card_bg border border-border_soft rounded-xl p-2 text-text_primary focus:ring-1 focus:ring-primary/50 text-sm" />
            {formData.image_url && <img src={formData.image_url} alt="Preview" className="h-16 mt-3 rounded-md border border-border_soft/50 shadow-sm" />}
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Last Verified</label>
              <input required type="date" value={formData.last_verified} onChange={e=>setFormData({...formData, last_verified: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Next Due</label>
              <input required type="date" value={formData.next_verification} onChange={e=>setFormData({...formData, next_verification: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Changes</button>
        </form>
      </div>
    </div>
  );
};

export default function Equipment() {
  const [equipment, setEquipment] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [formData, setFormData] = useState({ name: "", image_url: "" });

  const fetchEquipment = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/equipment`);
      setEquipment(res.data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchEquipment(); }, [fetchEquipment]);

  const handleVerify = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/equipment/${id}/verify`);
      fetchEquipment();
    } catch(e) { console.error(e); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/equipment`, formData);
      setFormData({ name: "", image_url: "" });
      setShowModal(false);
      fetchEquipment();
    } catch(e) { console.error(e); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/update/equipment/${data.id}`, data);
      setShowEdit(null);
      fetchEquipment();
    } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Delete this equipment permanently?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/equipment/${id}`);
        fetchEquipment();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Equipment</h1>
          <p className="text-text_secondary text-sm">Track physical assets and manage 15-day maintenance cycles.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md">
          <Plus size={20} /> Add Equipment
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {equipment.length === 0 && <p className="text-text_secondary col-span-full">No equipment registered yet.</p>}
        {equipment.map(eq => (
          <div key={eq.id} className={`bg-card_bg border rounded-2xl p-6 shadow-sm relative group transition-all duration-300 hover:-translate-y-1 ${eq.status === 'Overdue' ? 'border-accent_danger/40 hover:shadow-[0_8px_30px_rgba(248,113,113,0.1)]' : 'border-border_soft hover:border-primary/40'}`}>
            <div className="absolute top-6 right-6 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-card_bg/90 backdrop-blur-sm p-1.5 rounded-lg border border-border_soft/50 shadow-sm">
              <button onClick={() => setShowEdit(eq)} className="p-1.5 text-text_secondary hover:text-gold transition-colors" title="Edit"><Pencil size={16}/></button>
              <button onClick={() => handleDelete(eq.id)} className="p-1.5 text-text_secondary hover:text-accent_danger transition-colors" title="Delete"><Trash2 size={16}/></button>
            </div>
            
            {eq.image_url ? (
              <div className="h-40 w-full mb-6 rounded-xl overflow-hidden border border-border_soft/20 bg-main_bg">
                <img src={eq.image_url} alt={eq.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-all duration-500 hover:scale-105" />
              </div>
            ) : (
              <div className="h-40 w-full mb-6 rounded-xl bg-main_bg flex flex-col items-center justify-center border border-border_soft/20 text-text_secondary/40 shadow-inner">
                <ImageIcon size={36} className="mb-3 opacity-50"/>
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold">No Image</span>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <div className={`p-3 rounded-xl shadow-sm ${eq.status==='Verified' ? 'bg-accent_success/10 text-accent_success border border-accent_success/20' : 'bg-accent_danger/10 text-accent_danger border border-accent_danger/20'}`}>
                <Wrench size={22} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-text_primary tracking-tight">{eq.name}</h3>
            </div>
            
            <div className="space-y-3 mb-6 bg-main_bg p-4 rounded-xl border border-border_soft/20 shadow-inner">
              <div className="flex justify-between items-center text-sm">
                <span className="text-text_secondary font-medium">Last Verified</span>
                <span className="text-text_primary font-mono">{eq.last_verified}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-text_secondary font-medium">Next Due</span>
                <span className={`font-mono ${eq.status==='Verified' ? 'text-text_primary' : 'text-accent_danger font-bold'}`}>{eq.next_verification}</span>
              </div>
            </div>

            {eq.status !== 'Verified' ? (
              <button onClick={() => handleVerify(eq.id)} className="w-full bg-accent_danger/10 border border-accent_danger/30 text-accent_danger py-3.5 rounded-xl font-bold text-sm hover:bg-accent_danger hover:text-text_primary transition-all flex justify-center items-center gap-2 shadow-sm">
                <ShieldAlert size={18} /> Acknowledge Maintenance
              </button>
            ) : (
              <div className="w-full bg-accent_success/10 border border-accent_success/30 text-accent_success py-3.5 rounded-xl font-bold text-sm flex justify-center items-center gap-2 cursor-pointer hover:bg-accent_success/20 transition-colors shadow-sm" onClick={() => handleVerify(eq.id)}>
                <ShieldCheck size={18} /> Safety Verified
              </div>
            )}
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-sm relative shadow-2xl overflow-y-auto max-h-[90vh]">
            <button onClick={() => setShowModal(false)} className="absolute top-6 right-6 text-text_secondary hover:text-gold transition-colors"><X size={20}/></button>
            <h2 className="text-2xl font-serif text-gold mb-6">Add Equipment</h2>
            <form onSubmit={handleAdd} className="space-y-5">
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Machine / Asset Name</label>
                <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Upload Image (Optional)</label>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, setFormData)} className="w-full bg-card_bg border border-border_soft rounded-xl p-2 text-text_primary focus:ring-1 focus:ring-primary/50 text-sm" />
                {formData.image_url && <img src={formData.image_url} alt="Preview" className="h-16 mt-3 rounded-md border border-border_soft/50 shadow-sm" />}
              </div>
              <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
